import { CLIENT_NAMES } from '@appConstants';
import constants from './constants';
import { NON_ALPHANUMERIC_REGEX } from '../constRegex';

const isSaiaClient = (configJSON) =>
  configJSON?.proValidation === CLIENT_NAMES.SAIA ||
  configJSON?.gridColumnConfig === CLIENT_NAMES.SAIA;

/**
 * Max length for dispatch creatable route names.
 * Reads mask from configJSON.routeNameEditParams.
 * SAIA only: length before `_` when present. All other clients: full mask.length (IBPlanning).
 * Falls back to ROUTE_NAME_MAX_LENGTH when mask is missing/empty.
 */
export const getRouteNameMaxLengthFromConfig = ({ configJSON } = {}) => {
  const mask = configJSON?.routeNameEditParams?.mask;
  if (!Array.isArray(mask) || mask.length === 0) {
    return constants.ROUTE_NAME_MAX_LENGTH;
  }
  if (isSaiaClient(configJSON)) {
    const underscoreIndex = mask.findIndex((slot) => slot === '_');
    if (underscoreIndex >= 0) {
      return underscoreIndex;
    }
  }
  return mask.length;
};

/**
 * From Creatable Select { value, isNewValue }: sanitize only when isNewValue; else return value as-is.
 * @param {object} sel
 * @param {number} [maxLength=ROUTE_NAME_MAX_LENGTH] Same cap as creatable input (from getRouteNameMaxLengthFromConfig).
 */
export const sanitizeRouteNameToAlphanumeric = (
  sel,
  maxLength = constants.ROUTE_NAME_MAX_LENGTH
) => {
  const { value, isNewValue } = sel || {};
  if (value == null || value === '') return null;
  if (!isNewValue) return value;
  const sanitized = String(value).replace(NON_ALPHANUMERIC_REGEX, '').slice(0, maxLength);
  return sanitized === '' ? null : sanitized;
};

// Exact match on mappedRouteName only — no last-N / no routeName fallback
export const isEquipmentRouteMatchingMappedRouteName = (equipmentRouteName, mappedRouteName) =>
  Boolean(equipmentRouteName) &&
  Boolean(mappedRouteName) &&
  equipmentRouteName === mappedRouteName;

export const findRouteOptionByTrailerRoute = (routeList = [], trailerRouteName) => {
  if (!trailerRouteName) return null;
  return routeList.find((r) => isEquipmentRouteMatchingMappedRouteName(trailerRouteName, r.mappedRouteName)) ?? null;
};

// Equipment routeName → mapping routeName for dropdown/payload; unmapped keeps equipment name
export const resolveMappingRouteNameFromTrailer = (routeList = [], trailerRouteName) => {
  if (!trailerRouteName || trailerRouteName === constants.ROUTE_NAME_DEFAULT) return null;
  const option = findRouteOptionByTrailerRoute(routeList, trailerRouteName);
  return option?.value ?? trailerRouteName;
};

// Sanitize only when isNewValue, then map equipment → mapping routeName
export const resolveSelectedMappingRouteName = ({
  selection,
  routeList = [],
  maxLength = constants.ROUTE_NAME_MAX_LENGTH,
} = {}) => {
  const sanitized = sanitizeRouteNameToAlphanumeric(selection, maxLength);
  if (sanitized == null) return null;
  return resolveMappingRouteNameFromTrailer(routeList, sanitized);
};

// In routeList with mappedRouteName: match via mappedRouteName; else exact equipmentRouteName === selectedRouteName (unmapped / user-created)
export const isEquipmentRouteMatchingSelectedRoute = ({
  equipmentRouteName,
  selectedRouteName,
  routeList = [],
}) => {
  if (!selectedRouteName) return false;
  const option = routeList.find((r) => r.value === selectedRouteName);
  if (option?.mappedRouteName) {
    return isEquipmentRouteMatchingMappedRouteName(equipmentRouteName, option.mappedRouteName);
  }
  return equipmentRouteName === selectedRouteName;
};

const getRouteNameDisplayValue = ({
  routeName,
  routeOptions = [],
}) => {
  if (routeName == null || routeName === '' || routeName === constants.ROUTE_NAME_DEFAULT)
    return null;

  const optionWithMatchingRouteName = routeOptions.find((r) => r.value === routeName);
  if (optionWithMatchingRouteName) return optionWithMatchingRouteName.value;

  const optionWithMatchingMappedRouteName = findRouteOptionByTrailerRoute(routeOptions, routeName);
  if (optionWithMatchingMappedRouteName) return optionWithMatchingMappedRouteName.value;

  return routeName;
};

const ensureTrailerRouteInOptions = ({
  routeOptions,
  trailerRouteName,
}) => {
  if (!trailerRouteName || trailerRouteName === constants.ROUTE_NAME_DEFAULT)
    return routeOptions;
  const isDuplicate = routeOptions.some(
    (route) =>
      route.value === trailerRouteName ||
      isEquipmentRouteMatchingMappedRouteName(trailerRouteName, route.mappedRouteName)
  );

  if (isDuplicate) return routeOptions;
  const newOption = {
    label: trailerRouteName,
    value: trailerRouteName,
  };
  return [newOption, ...routeOptions];
}

/** Alphanumeric sort by option value (case-insensitive). Does not mutate input. */
export const sortRouteOptionsByValue = (routeOptions = []) =>
  [...routeOptions].sort((routeA, routeB) =>
    String(routeA?.value ?? '').localeCompare(String(routeB?.value ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  );

/** Show "Add Route" when trailer is selected AND (has no route OR is closed). Disabled when no trailer is selected — user must pick a trailer first. */
export const shouldShowAddRouteOption = ({
  trailer,
  enableNewRouteAssignmentForTrailer,
  isClosedTrailer = false,
}) =>
  Boolean(enableNewRouteAssignmentForTrailer) && Boolean(trailer?.trailerNumber) && (!trailer?.routeName || isClosedTrailer);

// Merges session-level user-created routes into route options so they persist after switching to another route.
// Appends unique user-created routes, then sorts the combined list alphanumeric by value.
export const mergeUserCreatedRoutes = (routeOptions, userCreatedRoutes = []) => {
  if (!userCreatedRoutes?.length) return routeOptions;
  const existingValues = new Set(routeOptions.map((r) => r.value));
  const newRoutes = userCreatedRoutes.filter((r) => !existingValues.has(r.value));
  return newRoutes.length
    ? sortRouteOptionsByValue([...routeOptions, ...newRoutes])
    : routeOptions;
};

// Resolves base route options for dropdown. User-created (typed): show full list. Appended from API: show only that route.
export const resolveBaseRouteOptions = ({
  filteredRouteOptions,
  trailer,
  routeList,
  trailerList,
}) => {
  if (filteredRouteOptions?.length > 0) return filteredRouteOptions;
  if (!trailer?.routeName) return routeList;
  const matchedTrailer = trailerList?.find((t) => t.value === trailer.trailerId);
  const equipmentRouteOnList = matchedTrailer?.routeName ?? null;
  const mappingOption = findRouteOptionByTrailerRoute(routeList, trailer.routeName);
  const isAppendedFromApi =
    !mappingOption &&
    equipmentRouteOnList != null &&
    equipmentRouteOnList === trailer.routeName;
  return isAppendedFromApi ? [] : routeList;
};

/** Route list plus route names from all trailers (for duplicate-check / full options). Single-pass filter. */
export const getRouteOptionsIncludingFromAllTrailers = ({
  routeList = [],
  allTrailers = [],
}) => {
  const existingRouteNames = new Set(
    routeList.flatMap((r) => [r.value, r.mappedRouteName].filter(Boolean))
  );
  const seenNames = new Set();
  const fromTrailers = allTrailers
    .map((t) => t.routeName)
    .filter((name) => {
      if (!name || name === constants.ROUTE_NAME_DEFAULT) return false;
      if (seenNames.has(name)) return false;
      seenNames.add(name);
      if (existingRouteNames.has(name)) return false;
      return true;
    })
    .map((name) => ({ label: name, value: name }));
  return fromTrailers.length ? [...routeList, ...fromTrailers] : routeList;
};

/** Full route list for duplicate-check: base (mapping + trailers) + user-created for this trailer. */
export const getAllRouteOptionsForDuplicateCheck = ({
  baseRouteOptions = [],
  userCreatedRoutes = [],
}) => {
  const existingValues = new Set(baseRouteOptions.map((r) => String(r?.value ?? '').trim()));
  const userCreated = userCreatedRoutes.filter(
    (r) => r?.value && !existingValues.has(String(r.value).trim())
  );
  return userCreated.length ? [...baseRouteOptions, ...userCreated] : baseRouteOptions;
};

/** Single call for Route dropdown allOptions: route list + all trailers + user-created for trailer. */
export const getRouteOptionsForDuplicateCheck = ({
  routeList = [],
  allTrailers = [],
  userCreatedRoutes = [],
}) =>
  getAllRouteOptionsForDuplicateCheck({
    baseRouteOptions: getRouteOptionsIncludingFromAllTrailers({
      routeList,
      allTrailers,
    }),
    userCreatedRoutes,
  });

// When changing trailer, returns the route name to use on the new trailer.
// Re-applies the current slot's user-created route if the new trailer also owns it; otherwise falls back to the new trailer's own route.
export const resolveRouteOnTrailerChange = ({
  currentTrailer,
  newTrailerData,
  userCreatedRoutesByTrailer,
  enableNewRouteAssignmentForTrailer,
  routeList = [],
}) => {
  if (!enableNewRouteAssignmentForTrailer || !currentTrailer?.routeName || !newTrailerData?.trailerId) {
    return resolveMappingRouteNameFromTrailer(routeList, newTrailerData?.routeName ?? null);
  }
  const currentRoutes = userCreatedRoutesByTrailer[currentTrailer.trailerId] || [];
  const newTrailerRoutes = userCreatedRoutesByTrailer[newTrailerData.trailerId] || [];
  const isUserCreatedRoute = currentRoutes.some((r) => r.value === currentTrailer.routeName);
  const newTrailerHasRoute = newTrailerRoutes.some((r) => r.value === currentTrailer.routeName);
  if (isUserCreatedRoute && newTrailerHasRoute) return currentTrailer.routeName;
  return resolveMappingRouteNameFromTrailer(routeList, newTrailerData?.routeName ?? null);
};

export {
  getRouteNameDisplayValue,
  ensureTrailerRouteInOptions,
}